import { EnumButtonName, EventProperties } from '@calm/evexia-analytics';
import {
    Box,
    Button,
    ButtonNoAnalytics,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItemTextProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ElementType, ReactNode } from 'react';

const styleLeftIcon = { display: 'flex', alignItems: 'center' };
const styleRightIcon = {
    justifyContent: 'end',
};

type Props = {
    divider?: boolean;
    reverseTextStyles?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    primaryText: string;
    secondaryText?: ReactNode;
    disablePadding?: boolean;
    sx?: SxProps<Theme>;
    wrapperSx?: SxProps<Theme>;
    leftIconSx?: SxProps<Theme>;
    rightIconSx?: SxProps<Theme>;
    primaryTypographyProps?: ListItemTextProps['primaryTypographyProps'];
    secondaryTypographyProps?: ListItemTextProps['secondaryTypographyProps'];
} & OptionalButton;

type OptionalButton =
    | ({
          component: ElementType<any>;
          disabled?: boolean;
      } & (
          | {
                href: string;
                openInNewTab?: boolean;
                onClick?: never;
            }
          | {
                href?: never;
                openInNewTab?: never;
                onClick: () => void;
            }
      ) &
          (
              | {
                    trackButton: EnumButtonName;
                    trackProps?: EventProperties;
                    skipTracking?: boolean;
                }
              | {
                    trackButton?: never;
                    trackProps?: never;
                    skipTracking: true;
                }
          ))
    | {
          href?: never;
          openInNewTab?: never;
          component?: never;
          trackButton?: never;
          trackProps?: never;
          skipTracking?: never;
          onClick?: never;
          disabled?: never;
      };

const styleWrapperListItem = (disablePadding?: boolean) => ({
    display: 'flex',
    ...(disablePadding
        ? {}
        : {
              paddingLeft: { xs: 4, md: 8 },
              paddingRight: { xs: 4, md: 8 },
          }),
});

function WrapperListItem({
    trackButton,
    trackProps,
    skipTracking,
    children,
    onClick,
    href,
    openInNewTab,
    component,
    disablePadding,
    disabled,
    sx,
}: {
    children: ReactNode;
    disablePadding?: boolean;
    sx?: SxProps<Theme>;
} & Pick<
    OptionalButton,
    | 'href'
    | 'openInNewTab'
    | 'component'
    | 'trackButton'
    | 'onClick'
    | 'trackProps'
    | 'skipTracking'
    | 'disabled'
>) {
    const buttonRequirements = (trackButton || skipTracking) && component;
    const buttonActions = href || onClick;
    if (!buttonRequirements || !buttonActions) {
        return <Box sx={styleWrapperListItem(disablePadding)}>{children}</Box>;
    }

    const trackingProps = skipTracking
        ? { skipTracking: true }
        : {
              trackButton,
              trackProps,
              skipTracking: false,
          };
    // Nesting a withButtonAnalytics component inside ListItemButton causes double tracking
    const componentToUse = component === Button ? ButtonNoAnalytics : component;

    if (onClick) {
        return (
            <ListItemButton
                {...trackingProps}
                onClick={onClick}
                component={componentToUse}
                disabled={disabled}
                sx={sxAppend(styleWrapperListItem(disablePadding), sx)}
            >
                {children}
            </ListItemButton>
        );
    }

    return (
        <ListItemButton
            {...trackingProps}
            href={href}
            {...(openInNewTab ? { target: '_blank' } : {})}
            component={componentToUse}
            sx={sxAppend(styleWrapperListItem(disablePadding), sx)}
        >
            {children}
        </ListItemButton>
    );
}

export default function SimpleListItem({
    divider,
    trackButton,
    trackProps,
    skipTracking,
    reverseTextStyles,
    leftIcon,
    leftIconSx,
    rightIcon,
    rightIconSx,
    primaryText,
    primaryTypographyProps,
    secondaryText,
    secondaryTypographyProps,
    disablePadding,
    disabled,
    href,
    openInNewTab,
    onClick,
    component,
    sx,
    wrapperSx,
}: Props) {
    return (
        <ListItem
            aria-label={primaryText}
            disablePadding={disablePadding}
            divider={divider}
            sx={sx}
        >
            <WrapperListItem
                href={href}
                openInNewTab={openInNewTab}
                trackButton={trackButton}
                trackProps={trackProps}
                skipTracking={skipTracking}
                component={component}
                onClick={onClick}
                disabled={disabled}
                disablePadding={disablePadding}
                sx={wrapperSx}
            >
                {leftIcon && (
                    <ListItemAvatar sx={sxAppend(styleLeftIcon, leftIconSx)}>
                        {leftIcon}
                    </ListItemAvatar>
                )}
                <ListItemText
                    primary={primaryText}
                    primaryTypographyProps={{
                        variant: reverseTextStyles
                            ? 'subtitle2'
                            : 'buttonLarge',
                        component: 'div',
                        ...primaryTypographyProps,
                    }}
                    secondary={secondaryText}
                    secondaryTypographyProps={{
                        variant: reverseTextStyles
                            ? 'buttonLarge'
                            : 'subtitle2',
                        component: 'div',
                        ...secondaryTypographyProps,
                    }}
                    sx={
                        {
                            '.MuiListItemText-primary': {
                                color: reverseTextStyles
                                    ? 'text.sub'
                                    : 'text.body',
                                ...primaryTypographyProps?.sx,
                            },
                            '.MuiListItemText-secondary': {
                                color: reverseTextStyles
                                    ? 'text.body'
                                    : 'text.sub',
                                ...secondaryTypographyProps?.sx,
                            },
                        } as SxProps<Theme>
                    }
                />
                {rightIcon && (
                    <ListItemIcon sx={sxAppend(styleRightIcon, rightIconSx)}>
                        {rightIcon}
                    </ListItemIcon>
                )}
            </WrapperListItem>
        </ListItem>
    );
}
